import { 
  doc, 
  collection, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  deleteDoc
} from 'firebase/firestore';
import { db } from './index';

/**
 * Salva um token FCM para um usuário específico
 * @param {string} userId ID do usuário
 * @param {string} token Token FCM a ser salvo
 * @param {string} deviceInfo Informações do dispositivo (opcional)
 * @returns {Promise<boolean>} Indica se a operação foi bem-sucedida
 */
export const saveUserToken = async (userId, token, deviceInfo = {}) => {
  try {
    if (!userId || !token) {
      console.error('userId e token são obrigatórios');
      return false;
    }

    const deviceData = {
      userId,
      token,
      createdAt: serverTimestamp(),
      lastSeen: serverTimestamp(),
      ...deviceInfo,
      platform: deviceInfo.platform || navigator.platform,
      userAgent: deviceInfo.userAgent || navigator.userAgent
    };

    // Salvar no documento na coleção de tokens
    const tokenRef = doc(db, `fcmTokens/${token}`);
    await setDoc(tokenRef, deviceData);
    
    return true;
  } catch (error) {
    console.error('Erro ao salvar token de notificação:', error);
    return false;
  }
};

/**
 * Remove um token FCM para um usuário específico
 * @param {string} token Token FCM a ser removido
 * @returns {Promise<boolean>} Indica se a operação foi bem-sucedida
 */
export const removeUserToken = async (token) => {
  try {
    if (!token) {
      console.error('token é obrigatório');
      return false;
    }

    // Remover o token da coleção
    const tokenRef = doc(db, `fcmTokens/${token}`);
    await deleteDoc(tokenRef);
    
    return true;
  } catch (error) {
    console.error('Erro ao remover token de notificação:', error);
    return false;
  }
};

/**
 * Salva preferências de notificação para um usuário
 * @param {string} userId ID do usuário
 * @param {Object} preferences Objeto com preferências de notificação
 * @returns {Promise<boolean>} Indica se a operação foi bem-sucedida
 */
export const saveNotificationPreferences = async (userId, preferences) => {
  try {
    if (!userId) {
      console.error('userId é obrigatório');
      return false;
    }

    // Verificar se o documento de configurações já existe
    const prefsRef = doc(db, `notificationPreferences/${userId}`);
    const prefsSnap = await getDoc(prefsRef);
    
    if (prefsSnap.exists()) {
      // Atualizar documento existente
      await updateDoc(prefsRef, {
        ...preferences,
        updatedAt: serverTimestamp()
      });
    } else {
      // Criar novo documento
      await setDoc(prefsRef, {
        ...preferences,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao salvar preferências de notificação:', error);
    return false;
  }
};

/**
 * Obtém preferências de notificação para um usuário
 * @param {string} userId ID do usuário
 * @returns {Promise<Object|null>} Objeto com preferências ou null em caso de erro
 */
export const getNotificationPreferences = async (userId) => {
  try {
    if (!userId) {
      console.error('userId é obrigatório');
      return null;
    }

    const prefsRef = doc(db, `notificationPreferences/${userId}`);
    const prefsSnap = await getDoc(prefsRef);
    
    if (prefsSnap.exists()) {
      return prefsSnap.data();
    } else {
      // Retornar preferências padrão se não existir
      return {
        newAppointments: true,
        canceledAppointments: true,
        rescheduledAppointments: true,
        appointmentReminders: true
      };
    }
  } catch (error) {
    console.error('Erro ao obter preferências de notificação:', error);
    return null;
  }
}; 